<template>
    <v-card flat :disabled="disabled" class="booking-card text-grey-darken-5">
        <v-card-text class="px-0 py-5">
            <section class="d-flex align-center mb-3">
                <h2 class="d-flex align-center">
                    <v-icon icon="mdi-calendar-month-outline" size="20px" class="mr-2" />
                    <span class="text-h6 font-weight-bold">{{ capitalize(toLocaleLongDateString(modelValue.activeDate)) }}</span>
                </h2>
                <v-spacer />
                <div>
                    <v-dialog v-model="dialog" width="500">
                        <template #activator="{ props }">
                            <v-btn
                                v-bind="props"
                                icon
                                variant="flat"
                                size="x-small"
                                color="grey-lighten-3"
                                :disabled="!modelValue.isCancellationAllowed || disabled"
                            >
                                <v-icon icon="kdi-trash-can-outline" size="24px" color="error" />
                            </v-btn>
                        </template>

                        <template #default="{ isActive }">
                            <v-card rounded="lg">
                                <v-card-title class="d-flex justify-center pt-4 pb-0 px-6">
                                    <div class="text-h3">{{ t('yourBookings.cancelDialogTitle') }}</div>
                                </v-card-title>
                                <v-card-text class="text-center py-4 pt-4 pb-8">
                                    <span class="text-body-1 font-weight-regular text-secondary">
                                        {{ t('yourBookings.cancelDialogDescription', { quayName: modelValue.pickupQuayName }) }}
                                    </span>
                                </v-card-text>
                                <v-divider class="opacity-100 mx-6" />
                                <v-card-actions color="secondary" class="px-6 pt-6 pb-4">
                                    <v-defaults-provider :defaults="actionBtnOptions">
                                        <v-row>
                                            <v-col cols="6">
                                                <v-btn
                                                    color="primary"
                                                    variant="outlined"
                                                    data-id="bookingCancelDialogDismissBtn"
                                                    @click="isActive.value = false"
                                                >
                                                    {{ t('form.cancelDialogDismissBtnLabel', 'Cancel') }}
                                                </v-btn>
                                            </v-col>
                                            <v-col cols="6">
                                                <v-btn
                                                    color="red-darken-2"
                                                    variant="flat"
                                                    data-id="bookingCancelDialogConfirmBtn"
                                                    @click="handleCancelBooking"
                                                >
                                                    {{ t('yourBookings.cancelDialogConfirmationBtnLabel', 'Cancel booking') }}
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-defaults-provider>
                                </v-card-actions>
                            </v-card>
                        </template>
                    </v-dialog>
                </div>
            </section>
            <section class="mb-3">
                <h3 class="text-body-1 font-weight-regular text-grey-darken-3 mb-1">
                    {{ t('yourBookings.bookingDepartureQuayLabel', 'From') }}
                </h3>
                <div class="d-flex align-center">
                    <v-sheet width="24px" class="mr-2">
                        <v-icon :icon="stopPlaceTypeIcon" size="32px" class="ml-n1 mt-n1" />
                    </v-sheet>
                    <div class="text-h6">{{ modelValue.pickupQuayName }}</div>
                </div>
            </section>
            <section>
                <h3 class="text-body-1 font-weight-regular text-grey-darken-3 mb-1">
                    {{ t('yourBookings.bookingDestinationDisplayLabel', 'Departure') }}
                </h3>
                <div class="d-flex align-center">
                    <div class="d-flex">
                        <mode-chip :title="modelValue.lineName" class="mr-2" />
                        <trip-headsign :title="destinationParts[0]" :subtitle="destinationParts[1]" />
                    </div>
                    <v-spacer />
                    <div class="text-h5">{{ toLocaleTimeString(modelValue.pickupTime) }}</div>
                </div>
            </section>
        </v-card-text>
    </v-card>
</template>

<script setup lang="ts">
import { useStringHelpers } from '@/features/common/composables';
import { BookingDto } from '../../types';
import { toLocaleTimeString, toLocaleLongDateString } from '@geta/kolumbus-frontend/composables';
import { capitalize } from 'lodash-es';
import { computed, ref } from 'vue';
import TripHeadsign from './TripHeadsign.vue';
import ModeChip from './ModeChip.vue';
import { useI18n } from 'vue-i18n';

interface IBookingCard {
    modelValue: BookingDto;
    disabled?: boolean;
}

interface IBookingCardEmits {
    (e: 'click:cancel-booking'): void;
}

const props = defineProps<IBookingCard>();
const emit = defineEmits<IBookingCardEmits>();

const { t } = useI18n();
const { splitAtWord } = useStringHelpers();
const destinationParts = computed(() => splitAtWord(props.modelValue.destinationDisplay));
const actionBtnOptions = {
    VBtn: {
        block: true,
        size: 'x-large',
        rounded: 'lg',
        class: 'text-h6 font-weight-bold'
    }
};
const dialog = ref(false);
const stopPlaceTypeIcon = computed(() => {
    switch (props.modelValue.stopPlaceTransitInfo.stopPlaceType) {
        case 2:
            return 'kdi-bus';
        case 3:
        case 4:
        case 7:
        case 10:
            return 'kdi-rail';
        case 5:
            return 'kdi-speedboat';
        case 11:
            return 'kdi-water';
        default:
            return undefined;
    }
});

function handleCancelBooking() {
    dialog.value = false;
    emit('click:cancel-booking');
}
</script>
